import { GetConformingCloudAssetsRequest } from "../../../../assets/api/GetConformingCloudAssetsRequest";
import { GetConformingCloudAssetsResponse } from "../../../../assets/api/GetConformingCloudAssetsResponse";
import { GetConformingOnPremAssetsRequest } from "../../../../assets/api/GetConformingOnPremAssetsRequest";
import { GetConformingOnPremAssetsResponse } from "../../../../assets/api/GetConformingOnPremAssetsResponse";
import { cacheRequests } from "../../../utils/cacheRequests";
import { GetCloudFileProxiesRequest } from "../cloud-files/get-proxies/GetCloudFileProxiesRequest";
import { GetCloudFileProxiesResponse } from "../cloud-files/get-proxies/GetCloudFileProxiesResponse";
import { useCloudFileApi } from "../cloud-files/useCloudFileApi";
import { GetCloudAssetDetailsRequest } from "./details/GetCloudAssetDetailsRequest";
import { GetCloudAssetDetailsResponse } from "./details/GetCloudAssetDetailsResponse";
import { GetOnPremAssetDetailsRequest } from "./details/GetOnPremAssetDetailsRequest";
import { GetOnPremAssetDetailsResponse } from "./details/GetOnPremAssetDetailsResponse";
import { UpdateCloudSoundtrackAssetLanguageRequest } from "./details/UpdateCloudSoundtrackAssetLanguageRequest";
import { UpdateCloudSoundtrackAssetLanguageResponse } from "./details/UpdateCloudSoundtrackAssetLanguageResponse";
import { UpdateCloudSubtitleAssetLanguageRequest } from "./details/UpdateCloudSubtitleAssetLanguageRequest";
import { UpdateCloudSubtitleAssetLanguageResponse } from "./details/UpdateCloudSubtitleAssetLanguageResponse";
import { UpdateCloudVideoAssetSubtitleRequest } from "./details/UpdateCloudVideoAssetSubtitleRequest";
import { UpdateCloudVideoAssetSubtitleResponse } from "./details/UpdateCloudVideoAssetSubtitleResponse";
import { useAssetsApi } from "./useAssetsApi";

const getCloudAssetDetailsCache = cacheRequests<GetCloudAssetDetailsResponse>();
const getOnPremAssetDetailsCache = cacheRequests<GetOnPremAssetDetailsResponse>();
const getConformingCloudAssetsCache = cacheRequests<GetConformingCloudAssetsResponse>();
const getConformingOnPremAssetsCache = cacheRequests<GetConformingOnPremAssetsResponse>();
const getCloudFileProxiesCache = cacheRequests<GetCloudFileProxiesResponse>();

export const useCachedAssetsApi = () => {
    const {
        getCloudAssetDetails,
        getOnPremAssetDetails,
        getConformingCloudAssets,
        getConformingOnPremAssets,
        updateCloudSubtitleAssetLanguage: updateCloudSubtitleAssetLanguageUncached,
        updateCloudSoundtrackAssetLanguage: updateCloudSoundtrackAssetLanguageUncached,
        updateCloudVideoAssetSubtitle: updateCloudVideoAssetSubtitleUncached
    } = useAssetsApi();

    const { getCloudFileProxies } = useCloudFileApi();

    const ttlMs = 20000;

    const getCachedCloudAssetDetails = async (
        request: GetCloudAssetDetailsRequest
    ): Promise<GetCloudAssetDetailsResponse> => {
        return getCloudAssetDetailsCache.getRequest({
            requestFn: () => getCloudAssetDetails(request),
            key: `cloud-asset-details-${request.assetId}`,
            ttlMs
        });
    };

    const updateCloudSubtitleAssetLanguage = async (
        request: UpdateCloudSubtitleAssetLanguageRequest
    ): Promise<UpdateCloudSubtitleAssetLanguageResponse> => {
        const response = await updateCloudSubtitleAssetLanguageUncached(request);

        const key = getCloudAssetDetailsCacheKey(request.assetId);
        getCloudAssetDetailsCache.evictCacheByKey(key);

        return response;
    };

    const updateCloudSoundtrackAssetLanguage = async (
        request: UpdateCloudSoundtrackAssetLanguageRequest
    ): Promise<UpdateCloudSoundtrackAssetLanguageResponse> => {
        const response = await updateCloudSoundtrackAssetLanguageUncached(request);

        const key = getCloudAssetDetailsCacheKey(request.assetId);
        getCloudAssetDetailsCache.evictCacheByKey(key);

        return response;
    };

    const updateCloudVideoAssetSubtitle = async (
        request: UpdateCloudVideoAssetSubtitleRequest
    ): Promise<UpdateCloudVideoAssetSubtitleResponse> => {
        const response = await updateCloudVideoAssetSubtitleUncached(request);

        const key = getCloudAssetDetailsCacheKey(request.assetId);
        getCloudAssetDetailsCache.evictCacheByKey(key);

        return response;
    };

    const getCachedCloudFileProxies = async (
        request: GetCloudFileProxiesRequest
    ): Promise<GetCloudFileProxiesResponse> => {
        return getCloudFileProxiesCache.getRequest({
            requestFn: () => getCloudFileProxies(request),
            key: `cloud-file-proxies-${request.cloudFileId}`,
            ttlMs
        });
    };

    const getCachedOnPremAssetDetails = async (
        request: GetOnPremAssetDetailsRequest
    ): Promise<GetOnPremAssetDetailsResponse> => {
        return getOnPremAssetDetailsCache.getRequest({
            requestFn: () => getOnPremAssetDetails(request),
            key: `on-prem-asset-details-${request.assetId}`,
            ttlMs
        });
    };

    const getCachedConformingCloudAssets = async (
        request: GetConformingCloudAssetsRequest
    ): Promise<GetConformingCloudAssetsResponse> => {
        return getConformingCloudAssetsCache.getRequest({
            requestFn: () => getConformingCloudAssets(request),
            key: `cloud-conforming-assets-${request.assetId}`,
            ttlMs
        });
    };

    const getCachedConformingOnPremAssets = async (
        request: GetConformingOnPremAssetsRequest
    ): Promise<GetConformingOnPremAssetsResponse> => {
        return getConformingOnPremAssetsCache.getRequest({
            requestFn: () => getConformingOnPremAssets(request),
            key: `on-prem-conforming-assets-${request.assetId}`,
            ttlMs
        });
    };

    return {
        getCloudAssetDetails: getCachedCloudAssetDetails,
        getOnPremAssetDetails: getCachedOnPremAssetDetails,
        getConformingCloudAssets: getCachedConformingCloudAssets,
        getConformingOnPremAssets: getCachedConformingOnPremAssets,
        getCloudFileProxies: getCachedCloudFileProxies,
        updateCloudSubtitleAssetLanguage,
        updateCloudVideoAssetSubtitle,
        updateCloudSoundtrackAssetLanguage
    };
};

const getCloudAssetDetailsCacheKey = (cloudAssetId: string) => `cloud-asset-details-${cloudAssetId}`;
